import {DotFillIcon} from '@primer/octicons-react'
import {memo} from 'react'
import type {ArtifactState} from '../types/artifact'
import type {SessionState} from '../types/session'
import type {UITask} from '../types/task'
import {getLeadingVisualIconAttributes, type IconAttributes} from '../utils/get-leading-visual-icon-attributes'
import styles from './TaskLeadingVisual.module.css'

export interface TaskLeadingVisualProps {
  isOptimistic?: boolean
  prState?: ArtifactState
  prInMergeQueue?: boolean
  sessionState?: SessionState
  agentSlug?: UITask['agentSlug']
  isArchived?: boolean
  className?: string
}

/**
 * Displays a leading visual icon for a task/session based on its current state.
 * Only handles state-based icons (PR status, session status, etc.).
 * For agent-type-specific icons, callers should render AgentProfile.slots.LeadingIcon directly.
 *
 * This component is memoized to prevent the loading animation from resetting
 * when other tasks in a list update.
 */
export const TaskLeadingVisual = memo(function TaskLeadingVisualComponent({
  isOptimistic = false,
  prState,
  prInMergeQueue,
  sessionState,
  agentSlug,
  isArchived,
  className,
}: TaskLeadingVisualProps) {
  const iconAttributes: IconAttributes = isOptimistic
    ? {icon: DotFillIcon, color: 'var(--fgColor-attention)', 'data-testid': 'icon-dot'}
    : getLeadingVisualIconAttributes(prState, prInMergeQueue, sessionState, isArchived, agentSlug)

  const Icon = iconAttributes.icon

  return (
    <div
      className={`${styles.iconWrapper} ${className ?? ''}`}
      style={{
        color: iconAttributes.color,
        marginTop: iconAttributes.marginTop,
      }}
      data-testid={iconAttributes['data-testid']}
    >
      <Icon />
    </div>
  )
})
